import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBackIosRounded,
  AttachFileRounded,
  CheckRounded,
  Download,
  SpeakerNotesRounded,
} from "@mui/icons-material";
import ColumnContainer from "../../../../common/ColumnContainer";
import { TableWrapper, TitleContainer } from "../../../style";
import RowContainer from "../../../../common/RowContainer";
import { DashboardButton } from "../../../../common/DashboardButton";
import { adminDashboardRoute } from "../../../../../utils/routes/Routes";
import {
  getCurrentProjectId,
  selectSingleProject,
} from "../../../../../redux/features/projects/projectsSlice";
import { useUpdateSingleProjectMutation } from "../../../../../redux/features/projects/projectsApi";
import SelectComponent from "../../../../common/SelectComponent";
import UploadFileComponent from "../../../../common/UploadFileComponent";
import { SnackbarComponent } from "../../../../common/SnackbarComponent";
import { handleDownloadClick } from "../../../../../utils/helpers/downloadFile";
import { useGetProjectByProjectIdQuery } from "../../../../../redux/features/projects/projectsApi";
import { FormatDateTime } from "../../../../../utils/helpers/FormatDateTime";
import { FormattedMessage } from "react-intl";
import { SkeletonLoading } from "../../../../common/SkeletonLoading";
import { ErrorLoading } from "../../../../common/ErrorLoading";
import { translateStatusString } from "../../../../../utils/helpers/translateStatusString";
import { selectLanguage } from "../../../../../redux/features/language/languageSlice";

export const ProjectDetails = () => {
  // INIT
  const { id: userId, projectId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentProject = useSelector(selectSingleProject);
  const currentLanguage = useSelector(selectLanguage);

  // REDUX
  const {
    data,
    isLoading,
    isError,
    refetch: refetchSlaveProject,
  } = useGetProjectByProjectIdQuery({
    projectId,
  });

  // STATE
  const [projectStatus, setProjectStatus] = useState(data?.status || null);
  const [fileUploaded, setFileUploaded] = useState(null);
  const [fileInputRef, setFileInputRef] = useState(null);
  const [openSnackbarFile, setOpenSnackbarFile] = useState(false);
  const [openSnackbarUpdate, setOpenSnackbarUpdate] = useState(false);
  const [validFormatFile, setValidFormatFile] = useState(true);
  const [mappedData, setMappedData] = useState(null);
  const [errorSizeFile, setErrorSizeFile] = useState(false);

  // EFFECTS
  useEffect(() => {
    dispatch(getCurrentProjectId(projectId));
  }, [projectId, dispatch]);

  useEffect(() => {
    setValidFormatFile(false);
  }, []);
  const [
    updateProject,
    {
      isLoading: uploadIsLoading,
      isError: uploadIsError,
      isSuccess: uploadIsSuccess,
    },
  ] = useUpdateSingleProjectMutation();
  const handleProjectStatusChange = (event) => {
    event.preventDefault();
    setProjectStatus(event.target.value);
  };

  useEffect(() => {
    refetchSlaveProject({ projectId });
  }, [, projectId, refetchSlaveProject, location.pathname]);

  useEffect(() => {
    if (data?.status) {
      setProjectStatus(data.status);
    }
  }, [data?.status]);

  useEffect(() => {
    if ((uploadIsError || uploadIsSuccess) && !uploadIsLoading) {
      setOpenSnackbarUpdate(true);
    } else if (!uploadIsError && !uploadIsSuccess && !uploadIsLoading) {
      setOpenSnackbarUpdate(false);
    }
  }, [uploadIsError, uploadIsSuccess, uploadIsLoading]);

  useEffect(() => {
    if (fileUploaded) {
      setValidFormatFile(true);
    }
  }, [fileUploaded]);
  // FUNCTIONS
  const handleFileUpload = (fileUploaded) => {
    if (validFormatFile) {
      setFileUploaded(fileUploaded);
    }
  };

  const handleFileChange = (file) => {
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();
      if (["zip", "bin", "mod"].includes(fileExtension)) {
        setFileUploaded(file);
      } else {
        setOpenSnackbarFile(true);
        setValidFormatFile(false);
      }
    }
  };

  const handleFileInputRefChange = (fileInput) => {
    setFileInputRef(fileInput);
  };

  const handleFileSizeError = (isError) => {
    setErrorSizeFile(isError);
  };

  const handleSave = async () => {
    const formData = new FormData();
    formData.append("is_admin_mod_file", true);
    if (projectStatus !== data?.status) {
      formData.append("project_status", projectStatus);
    }
    if (fileUploaded && validFormatFile) {
      formData.append("file", fileUploaded);
    }
    if (formData.has("project_status") || formData.has("file")) {
      try {
        await updateProject({
          projectId: projectId,
          userId: userId,
          formData: formData,
        }).unwrap();
      } catch (error) {
        console.error("Failed to update project:", error);
      }
    }
  };

  const handleGoBackToProjects = () => {
    navigate(`${adminDashboardRoute.users}/${userId}`);
  };

  const renderTickets = () => {
    return (
      <ColumnContainer halfGap>
        {data?.tickets?.map((ticket, index) => {
          return (
            <RowContainer align="center" start minGap>
              <Tooltip title={<FormattedMessage id="tooltip.viewTicket" />}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <SpeakerNotesRounded />
                  <Link to={`${adminDashboardRoute.tickets}/${ticket.id}`}>
                    <Typography
                      key={index}
                      variant="p"
                      component="p"
                      sx={{
                        cursor: "pointer",

                        "&:hover": {
                          color: "var(--main-color)",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Ticket #{ticket.id}
                    </Typography>
                  </Link>
                </div>
              </Tooltip>
            </RowContainer>
          );
        })}
      </ColumnContainer>
    );
  };

  // UTILS

  const titles = {
    created_at: <FormattedMessage id="project.created" />,
    modified_at: <FormattedMessage id="project.updated" />,
    status: <FormattedMessage id="project.status" />,
    v_type: <FormattedMessage id="project.type" />,
    brand: <FormattedMessage id="project.brand" />,
    model: <FormattedMessage id="project.model" />,
    generations: <FormattedMessage id="project.generation" />,
    engine: <FormattedMessage id="project.engine" />,
    engine_hp: <FormattedMessage id="project.engineHp" />,
    year: <FormattedMessage id="project.year" />,
    gearbox: <FormattedMessage id="project.gearbox" />,
    fuel: <FormattedMessage id="project.fuel" />,
    reading_method: <FormattedMessage id="project.readingMethod" />,
    ecu_brand: <FormattedMessage id="project.ecuBrand" />,
    ecu_name: <FormattedMessage id="project.ecuName" />,
    ecu_type: <FormattedMessage id="project.ecuType" />,
    services: <FormattedMessage id="project.services" />,
    dtc: <FormattedMessage id="project.dtc" />,
  };
  const textfieldProjMapped = (data) => {
    const arrayProjectTextfield = data?.project_textfield.split(", ");

    const mappedDataObj = {
      created_at: <FormatDateTime value={data?.created_at} />,
      modified_at: <FormatDateTime value={data?.modified_at} />,
      status: data?.status,
      v_type: arrayProjectTextfield[0] || "-",
      brand: arrayProjectTextfield[1] || "-",
      model: arrayProjectTextfield[2] || "-",
      generations: arrayProjectTextfield[3] || "-",
      engine: arrayProjectTextfield[4] || "-",
      engine_hp: arrayProjectTextfield[5] || "-",
      year: arrayProjectTextfield[6] || "-",
      gearbox: arrayProjectTextfield[7] || "-",
      fuel: arrayProjectTextfield[8] || "-",
      ecu_brand: arrayProjectTextfield[9] || "-",
      ecu_name: arrayProjectTextfield[10] || "-",
      ecu_type: arrayProjectTextfield[11] || "-",
      services: data?.services,
      dtc: data?.dtc,
    };
    return mappedDataObj;
  };

  const getNestedValue = (data, title) => {
    return data[title]?.name || data[title]?.engine_cv || data[title];
  };

  useEffect(() => {
    if (data) {
      setMappedData(data);
      if (data.project_textfield) {
        const result = textfieldProjMapped(data);

        setMappedData(result);
      }
    }
  }, [data]);

  return (
    <ColumnContainer className="user-details-project-details">
      {isLoading ? (
        <SkeletonLoading />
      ) : isError ? (
        <ErrorLoading />
      ) : mappedData ? (
        <>
          <RowContainer>
            <TitleContainer>
              <RowContainer minGap start align="center">
                <IconButton>
                  <ArrowBackIosRounded
                    onClick={() => handleGoBackToProjects()}
                  />
                </IconButton>
                <Typography variant="h3" component="h3">
                  <FormattedMessage id="project.title.singular" /> #{projectId}
                </Typography>
              </RowContainer>
            </TitleContainer>
          </RowContainer>
          <ColumnContainer halfGap sx={{ gap: "4rem" }}>
            <ColumnContainer sx={{ gap: "3rem" }}>
              <TableWrapper className="projects-details-table">
                {Object.entries(titles).map(([key, titleValue]) => {
                  const correctValue = getNestedValue(mappedData, key);
                  if (!!correctValue) {
                    return (
                      <Grid item xs={12} sm={6} md={4} key={key}>
                        <Typography
                          variant="h6"
                          component="h6"
                          fontSize="1rem"
                          fontWeight="bold"
                          color={"var(--main-color)"}
                        >
                          {titleValue}
                        </Typography>
                        <Typography variant="body1">
                          {key === "created_at" || key === "modified_at" ? (
                            <FormatDateTime value={correctValue} />
                          ) : key === "status" ? (
                            translateStatusString(correctValue, currentLanguage)
                          ) : (
                            correctValue.toString()
                          )}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                      </Grid>
                    );
                  }
                  return null;
                })}
              </TableWrapper>

              <Grid container spacing={2} columnSpacing={6}>
                <Grid item xs={12} sm={12} md={12} lg={6} height="fit-content">
                  <ColumnContainer halfGap className="attachement">
                    <Typography
                      variant="h6"
                      component="h6"
                      fontSize="1rem"
                      fontWeight="bold"
                      color={"var(--main-color)"}
                    >
                      <FormattedMessage id="file.toBeProcessed" />
                    </Typography>

                    <RowContainer
                      align="center"
                      minGap
                      className="file-container"
                    >
                      {data?.ori_file_id ? (
                        <RowContainer minGap align="center">
                          <AttachFileRounded
                            sx={{
                              "& path": {
                                color: "var(--text-primary)",
                              },
                            }}
                          />
                          <Typography
                            variant="p"
                            component="p"
                            color={"var(--text-primary)"}
                          >
                            {data?.ori_file_id}
                          </Typography>
                          <Tooltip
                            title={
                              <FormattedMessage id="tooltip.downloadFile" />
                            }
                          >
                            <div>
                              <IconButton
                                className="download-file-btn"
                                onClick={() =>
                                  handleDownloadClick(
                                    false,
                                    userId,
                                    data?.ori_file_id
                                  )
                                }
                              >
                                <Download />
                              </IconButton>
                            </div>
                          </Tooltip>
                        </RowContainer>
                      ) : (
                        <Typography variant="body1">-</Typography>
                      )}
                    </RowContainer>
                    <Divider
                      sx={{
                        borderColor: "#f5f5f5",
                      }}
                    />
                  </ColumnContainer>
                </Grid>
              </Grid>
              <Grid container spacing={4} columnSpacing={6}>
                <Grid item xs={12} sm={12} md={12} lg={12} height="fit-content">
                  <ColumnContainer halfGap className="ticket-list">
                    <RowContainer align="center">
                      <Typography
                        variant="h6"
                        component="h6"
                        fontSize="1rem"
                        fontWeight="bold"
                        color={"var(--main-color)"}
                      >
                        <FormattedMessage id="project.tickets" />
                      </Typography>
                    </RowContainer>

                    {data?.tickets?.length > 0
                      ? renderTickets()
                      : "Nessun ticket collegato"}
                  </ColumnContainer>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={12}
                  lg={12}
                  height="fit-content"
                  className="upload-grid-container"
                >
                  <UploadFileComponent
                    acceptedFormatsString={".zip, .mod, .bin"}
                    onFileUpload={handleFileUpload}
                    onFileChange={handleFileChange}
                    onFileInputRefChange={handleFileInputRefChange}
                    onFileSizeError={handleFileSizeError}
                    instructions={<FormattedMessage id="file.attach.admin" />}
                  >
                    <RowContainer className="success-upload-icons" center>
                      {data?.mod_file_id ||
                      currentProject?.mod_file_id ||
                      fileUploaded ? (
                        <RowContainer center minGap align="end">
                          <CheckRounded />
                          <Tooltip
                            title={
                              <FormattedMessage id="tooltip.downloadFile" />
                            }
                          >
                            <div>
                              <Typography
                                component="p"
                                fontSize={"16px"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleDownloadClick(
                                    false,
                                    userId,
                                    currentProject?.mod_file_id
                                  );
                                }}
                              >
                                {validFormatFile
                                  ? fileUploaded?.name
                                  : data?.mod_file_detail?.file_name}
                              </Typography>
                            </div>
                          </Tooltip>
                        </RowContainer>
                      ) : errorSizeFile ? (
                        <Typography
                          component="p"
                          fontSize={"16px"}
                          color={"var(--error)"}
                          textAlign={"center"}
                        >
                          {<FormattedMessage id="file.size.error" />}
                        </Typography>
                      ) : (
                        <Typography
                          component="p"
                          fontSize={"16px"}
                          color="var(--error)"
                        >
                          <FormattedMessage id="file.noFile" />
                        </Typography>
                      )}
                    </RowContainer>
                  </UploadFileComponent>
                </Grid>
                <SnackbarComponent
                  message={<FormattedMessage id="snackbar.file.format" />}
                  duration={5000}
                  open={openSnackbarFile}
                  handleClose={() => setOpenSnackbarFile(false)}
                ></SnackbarComponent>
              </Grid>
              <Grid container spacing={2} columnSpacing={6}>
                <Grid item xs={12} sm={12} md={6} lg={6} height="fit-content">
                  <ColumnContainer minGap>
                    <Typography
                      variant="h6"
                      component="h6"
                      fontSize="1rem"
                      fontWeight="bold"
                      color={"var(--main-color)"}
                    >
                      <FormattedMessage id="project.updateStatus" /> #
                      {projectId}:
                    </Typography>
                    <SelectComponent
                      width="200px"
                      value={projectStatus || ""}
                      onChange={(event) => handleProjectStatusChange(event)}
                    >
                      <MenuItem value="new">
                        <FormattedMessage id="status.new" />
                      </MenuItem>
                      <MenuItem value="completed">
                        <FormattedMessage id="status.completed" />
                      </MenuItem>
                      <MenuItem value="in_progress">
                        <FormattedMessage id="status.inProgress" />
                      </MenuItem>
                      <MenuItem value="pending">
                        <FormattedMessage id="status.pending" />
                      </MenuItem>
                      <MenuItem value="refunded">
                        <FormattedMessage id="status.refunded" />
                      </MenuItem>
                      <MenuItem value="failed">
                        <FormattedMessage id="status.failed" />
                      </MenuItem>
                    </SelectComponent>
                  </ColumnContainer>
                </Grid>
              </Grid>
              <RowContainer
                style={{ width: "100%", paddingBottom: "3rem" }}
                end
              >
                <RowContainer start halfGap align="center">
                  {uploadIsLoading ? <CircularProgress size={24} /> : null}
                  <DashboardButton
                    type="filled"
                    disabled={uploadIsLoading}
                    onClick={handleSave}
                  >
                    <FormattedMessage id="button.save" />
                  </DashboardButton>
                </RowContainer>
              </RowContainer>
              <SnackbarComponent
                message={
                  uploadIsSuccess ? (
                    <FormattedMessage id="snackbar.project.success" />
                  ) : uploadIsError ? (
                    <FormattedMessage id="snackbar.project.error" />
                  ) : (
                    ""
                  )
                }
                duration={5000}
                open={openSnackbarUpdate}
                handleClose={() => setOpenSnackbarUpdate(false)}
              ></SnackbarComponent>
            </ColumnContainer>
          </ColumnContainer>
        </>
      ) : null}
    </ColumnContainer>
  );
};
